import {useQuery} from '@tanstack/react-query'
import {Layers, Shield, Tag, Check, X} from 'lucide-react'
import {useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Stack} from '@/components/shared/Stack'
import {CodeText} from '@/components/shared/CodeText'
import {permissions} from '@/lib/permissions'
import {useAuth} from '@/contexts/auth-context'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatTimestamp} from '@/lib/date-utils'
import {ErrorBanner} from '@/components/ui/error-banner'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {AccessProfileStatus} from '@/types/access-profile'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {PageLayout} from '@/components/shared/PageLayout'
import {SeverityBadge} from '@/components/violations/SeverityBadge'
import {ViolationSubjectTable} from '@/types/violation'
import {MutedText} from '@/components/shared/MutedText'
import {IconText} from '@/components/shared/IconText'
import {CardSection} from '@/components/shared/CardSection'
import {TextLink} from '@/components/shared/TextLink'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Skeleton} from '@/components/ui/skeleton'
import {resolveTypeValueIcon} from '@/components/entity-type/entity-type-icon'

export function FunctionGroupDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.functionGroupDetail.pageTitle)
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()
    const canUpdate = hasPermission(permissions.functionGroups.update)
    const canViewAccessProfiles = hasPermission(permissions.functionGroups.view)

    const {data: functionGroup, isLoading, error} = useQuery({
        queryKey: ['function-group', id],
        queryFn: () => services.functionGroups.detail(id!),
        enabled: !!id,
    })

    const {
        functionGroupTypes,
        environmentTypes,
        accessProfileStatuses: profileStatuses,
        functionGroupResponsibilityTypes,
    } = useAppConfig()

    const {
        data: accessProfilesData,
        isLoading: apLoading,
        error: apError,
        refetch: apRefetch,
    } = useQuery({
        queryKey: ['function-group-access-profiles', id],
        queryFn: () => services.functionGroups.accessProfiles(id!),
        enabled: !!id && canViewAccessProfiles,
    })

    const getApStatusVariant = (status: AccessProfileStatus): BadgeVariant => {
        switch (status) {
            case AccessProfileStatus.ACTIVE: return BadgeVariant.SUCCESS
            case AccessProfileStatus.DEPRECATED: return BadgeVariant.SECONDARY
            default: return BadgeVariant.OUTLINE
        }
    }

    const HeaderIcon = resolveTypeValueIcon(functionGroupTypes, functionGroup?.functionGroupType, Layers)

    return (
        <PageLayout
            variant="detail"
            title={functionGroup?.name ?? i18n.functionGroupDetail.pageTitle}
            icon={HeaderIcon}
            backTo={true}
            loading={isLoading}
            data-testid="function-group-detail-page"
            error={error ?? (!isLoading ? notFoundIfMissing(functionGroup, 'FUNCTION_GROUP') : undefined)}
            description={functionGroup ? (
                <>
                    <Badge variant="outline">
                        {labelOf(functionGroupTypes, functionGroup.functionGroupType)}
                    </Badge>
                    <Badge variant={functionGroup.requestable ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                        {functionGroup.requestable ? i18n.functionGroupDetail.requestable : i18n.functionGroupDetail.notRequestable}
                    </Badge>
                    <SeverityBadge entityTable={ViolationSubjectTable.FUNCTION_GROUPS} entityId={functionGroup.id}/>
                </>
            ) : undefined}
        >
            {functionGroup && (<>
                {/* Details Card */}
                <DetailsCard
                    data-testid="card-function-group-details"
                    title={i18n.common.details}
                    description={i18n.functionGroupDetail.detailsDescription}
                    fields={[
                        {label: i18n.common.name, value: functionGroup.name},
                        {label: i18n.common.type, value: labelOf(functionGroupTypes, functionGroup.functionGroupType)},
                        ...(functionGroup.description ? [{label: i18n.common.description, value: functionGroup.description, fullWidth: true as const}] : []),
                        ...(functionGroup.parent ? [{label: i18n.functionGroupDetail.parent, value: functionGroup.parent.displayName}] : []),
                        {
                            label: i18n.functionGroupDetail.autoAssignable,
                            value: functionGroup.autoAssignable
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        {
                            label: i18n.functionGroupDetail.requestableLabel,
                            value: functionGroup.requestable
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        {
                            label: i18n.functionGroupDetail.expiryRequired,
                            value: functionGroup.expiryRequired
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        ...(functionGroup.maxDurationDays != null ? [{label: i18n.functionGroupDetail.maxDurationDays, value: String(functionGroup.maxDurationDays)}] : []),
                        {label: i18n.common.createdAt, value: formatTimestamp(functionGroup.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(functionGroup.updatedAt)},
                    ]}
                />

                <OwnersSection
                    entityType={OwnersEntityType.functionGroups}
                    entityId={id!}
                    canEdit={canUpdate}
                    responsibilityOptions={functionGroupResponsibilityTypes}
                />

                {/* Attributes */}
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-attributes">
                            <IconText icon={Tag} size="md">{i18n.common.attributes}</IconText>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <InlineAttributeEditor
                            entityType={AttributeEntityType.FUNCTION_GROUP}
                            entityId={id!}
                            entityTypeCode={functionGroup.functionGroupType}
                            canEdit={canUpdate}
                        />
                    </CardContent>
                </Card>

                {/* Access Profiles Card */}
                {canViewAccessProfiles && (
                    <CardSection
                        icon={Shield}
                        title={String(i18n.functionGroupDetail.accessProfiles.card.title)}
                        description={apLoading || !accessProfilesData ? undefined : String(
                            i18n.functionGroupDetail.accessProfiles.card.description({count: accessProfilesData.totalCount})
                        )}
                        data-testid="fg-access-profiles-card"
                    >
                        {apLoading && (
                            <Stack gap="sm">
                                {[0, 1, 2].map(i => (
                                    <Skeleton key={i} className="h-10 w-full"/>
                                ))}
                            </Stack>
                        )}
                        {!apLoading && apError && (
                            <Stack gap="sm">
                                <ErrorBanner error={apError}/>
                                <MutedText>{i18n.functionGroupDetail.accessProfiles.error}</MutedText>
                                <Button variant="outline" size="sm" onClick={() => apRefetch()} data-testid="btn-retry-ap">
                                    {i18n.common.retry}
                                </Button>
                            </Stack>
                        )}
                        {!apLoading && !apError && accessProfilesData && (
                            <>
                                {accessProfilesData.truncated && (
                                    <MutedText size="sm" block className="text-amber-600 dark:text-amber-400" data-testid="ap-warning-100">
                                        {String(i18n.functionGroupDetail.accessProfiles.warning100)}
                                    </MutedText>
                                )}
                                {accessProfilesData.items.length === 0 ? (
                                    <MutedText block centered data-testid="ap-empty-state">
                                        {String(i18n.functionGroupDetail.accessProfiles.empty)}
                                    </MutedText>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead data-testid="col-name">{i18n.functionGroupDetail.accessProfiles.columns.name}</TableHead>
                                                <TableHead data-testid="col-code">{i18n.functionGroupDetail.accessProfiles.columns.code}</TableHead>
                                                <TableHead data-testid="col-resource">{i18n.functionGroupDetail.accessProfiles.columns.resource}</TableHead>
                                                <TableHead data-testid="col-environment">{i18n.functionGroupDetail.accessProfiles.columns.environment}</TableHead>
                                                <TableHead data-testid="col-risk-level">{i18n.functionGroupDetail.accessProfiles.columns.riskLevel}</TableHead>
                                                <TableHead data-testid="col-status">{i18n.functionGroupDetail.accessProfiles.columns.status}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {accessProfilesData.items.map((ap) => (
                                                <TableRow key={ap.id} data-testid={`ap-row-${ap.accessProfile.id}`}>
                                                    <TableCell>
                                                        <TextLink
                                                            to={routes.accessGovernance.profiles.detail(ap.accessProfile.id)}
                                                            variant="bold"
                                                            testId={`link-ap-${ap.accessProfile.id}`}
                                                        >
                                                            {ap.accessProfile.displayName}
                                                        </TextLink>
                                                    </TableCell>
                                                    <TableCell>
                                                        <CodeText>{ap.accessProfile.code ?? ''}</CodeText>
                                                    </TableCell>
                                                    <TableCell>{ap.resource?.displayName ?? ''}</TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">
                                                            {labelOf(environmentTypes, ap.environment)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <RiskLevelBadge riskLevel={ap.riskLevel}/>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={getApStatusVariant(ap.status as AccessProfileStatus)}
                                                            data-testid={`ap-status-${ap.accessProfile.id}`}
                                                        >
                                                            {labelOf(profileStatuses, ap.status)}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </>
                        )}
                    </CardSection>
                )}
            </>)}
        </PageLayout>
    )
}
